import type { Feature, FeatureCollection, LineString, Point } from 'geojson'

import { VIEWPORT_BUFFER_RATIO } from './constants'
import type { DocPoint } from './features'

export type LngLatBounds = {
  west: number
  south: number
  east: number
  north: number
}

export const expandBounds = (bounds: LngLatBounds, bufferRatio = VIEWPORT_BUFFER_RATIO): LngLatBounds => {
  const lngPad = (bounds.east - bounds.west) * bufferRatio
  const latPad = (bounds.north - bounds.south) * bufferRatio
  return {
    west: bounds.west - lngPad,
    south: bounds.south - latPad,
    east: bounds.east + lngPad,
    north: bounds.north + latPad,
  }
}

type BoundsSource = {
  getBounds: () => {
    getWest: () => number
    getSouth: () => number
    getEast: () => number
    getNorth: () => number
  } | null
}

export const viewportBounds = (map: BoundsSource, bufferRatio = VIEWPORT_BUFFER_RATIO): LngLatBounds | null => {
  const bounds = map.getBounds()
  if (!bounds) return null
  return expandBounds(
    { west: bounds.getWest(), south: bounds.getSouth(), east: bounds.getEast(), north: bounds.getNorth() },
    bufferRatio
  )
}

export const isPointInBounds = (point: DocPoint, bounds: LngLatBounds): boolean =>
  point.lon >= bounds.west && point.lon <= bounds.east && point.lat >= bounds.south && point.lat <= bounds.north

export const pointsInBounds = (points: DocPoint[], indexes: number[], bounds: LngLatBounds): number[] =>
  indexes.filter(index => isPointInBounds(points[index], bounds))

export const viewportFeatureIndexes = (
  points: DocPoint[],
  indexes: number[],
  bounds: LngLatBounds | null,
  cull: boolean
): number[] => {
  if (!cull) return indexes
  if (!bounds) return []
  return pointsInBounds(points, indexes, bounds)
}

export const headingTip = (point: DocPoint): [number, number] | null => {
  const raw = point.props().rawcourse
  const course = raw == null || raw === '' ? Number.NaN : Number(raw)
  if (!Number.isFinite(course)) return null
  const meters = 12
  const rad = (course * Math.PI) / 180
  const dLat = (meters * Math.cos(rad)) / 111320
  const dLon = (meters * Math.sin(rad)) / (111320 * Math.cos((point.lat * Math.PI) / 180))
  return [point.lon + dLon, point.lat + dLat]
}

export const pointsToCollection = (
  points: DocPoint[],
  items: { featureIndex: number; position: number }[]
): FeatureCollection<Point> => ({
  type: 'FeatureCollection',
  features: items.map(
    (item): Feature<Point> => ({
      type: 'Feature',
      properties: {
        position: item.position,
        hasPhoto: points[item.featureIndex].image ? 1 : 0,
      },
      geometry: {
        type: 'Point',
        coordinates: [points[item.featureIndex].lon, points[item.featureIndex].lat],
      },
    })
  ),
})

export const trackLine = (
  points: DocPoint[],
  items: { featureIndex: number; position: number }[]
): FeatureCollection<LineString> => {
  const features: Feature<LineString>[] = []
  let run: [number, number][] = []
  const flush = () => {
    if (run.length >= 2) {
      features.push({ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: run } })
    }
    run = []
  }
  items.forEach((item, index) => {
    const previous = items[index - 1]
    const point = points[item.featureIndex]
    if (previous && (item.position !== previous.position + 1 || point.track !== points[previous.featureIndex].track)) {
      flush()
    }
    run.push([point.lon, point.lat])
  })
  flush()
  return { type: 'FeatureCollection', features }
}

export const fitBoundsFor = (points: DocPoint[], indexes: number[]): [[number, number], [number, number]] | null => {
  if (indexes.length === 0) return null
  let west = Infinity
  let east = -Infinity
  let south = Infinity
  let north = -Infinity
  for (const index of indexes) {
    const point = points[index]
    west = Math.min(west, point.lon)
    east = Math.max(east, point.lon)
    south = Math.min(south, point.lat)
    north = Math.max(north, point.lat)
  }
  if (west === east) {
    west -= 0.01
    east += 0.01
  }
  if (south === north) {
    south -= 0.01
    north += 0.01
  }
  return [
    [west, south],
    [east, north],
  ]
}
